import { skipToken } from "@reduxjs/toolkit/query";
import type { AccountId } from "@repo/app-types/entities/account";
import {
  ATTAINMENT_TREND_PERIODS,
  type AttainmentTrendMaxPeriod,
  type AttainmentTrendPeriod,
  type AttainmentTrendResult,
  type PeriodAverageScoreDeltas,
  type PeriodAverageScores,
  type TeammateAttainmentTrends,
  type TrendPeriodScores,
} from "@repo/app-types/entities/attainmentTrend";
import {
  asISODateTime,
  type PlaybookScope,
  type TeamPerformancePageFilter,
} from "@repo/app-types/entities/filters";
import type { GroupId } from "@repo/app-types/entities/group";
import type {
  PlaybookCriterionId,
  PlaybookId,
  PlaybookNodeId,
  PlaybookSectionId,
} from "@repo/app-types/entities/playbook";
import { PlaybookReportType } from "@repo/app-types/entities/playbookReport";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { DateTime } from "luxon";
import { useMemo } from "react";
import {
  useDashboardCriteriaAttainmentTrendsQuery,
  useDashboardPlaybookAttainmentTrendQuery,
  useDashboardPlaybookOutcomeAttainmentTrendQuery,
  usePlaybookDashboardTimeMetricsQuery,
  usePlaybookHighImpactCriteriaQuery,
  usePlaybookReportQuery,
  usePlaybookSectionAttainmentTrendQuery,
} from "../../../api/entities/performancePlaybook.js";

export interface UseDashboardDataResult {
  readonly calls: {
    readonly currentTotalCount: number | undefined;
    readonly previousTotalCount: number | undefined;
    readonly trend: readonly number[] | undefined;
    readonly isLoading: boolean;
  };
  readonly callLength: {
    readonly currentAverageSeconds: number | null | undefined;
    readonly previousAverageSeconds: number | null | undefined;
    readonly trendSeconds: readonly (number | null)[] | undefined;
    readonly isLoading: boolean;
  };
  readonly callLengthPerGreenOutcome: {
    readonly currentAverageSeconds: number | null | undefined;
    readonly previousAverageSeconds: number | null | undefined;
    readonly trendSeconds: readonly (number | null)[] | undefined;
    readonly isLoading: boolean;
  };
  readonly playbookAttainment: {
    readonly data: AttainmentTrendResult | undefined;
    readonly averageScore: PeriodAverageScores | undefined;
    readonly averageScoreDeltaWithPrevious:
      | PeriodAverageScoreDeltas
      | undefined;
    readonly isLoading: boolean;
  };
  readonly criteriaAttainment: {
    readonly data:
      | Record<PlaybookCriterionId, AttainmentTrendResult>
      | undefined;
    readonly averageScore:
      | Record<PlaybookCriterionId, PeriodAverageScores>
      | undefined;
    readonly averageScoreDeltaWithPrevious:
      | Record<PlaybookCriterionId, PeriodAverageScoreDeltas>
      | undefined;
    readonly isLoading: boolean;
  };
  readonly outcomeAttainment: {
    readonly data: AttainmentTrendResult | undefined;
    readonly averageScore: PeriodAverageScores | undefined;
    readonly averageScoreDeltaWithPrevious:
      | PeriodAverageScoreDeltas
      | undefined;
    readonly isLoading: boolean;
  };
  readonly sectionsAttainment: {
    readonly data: Record<PlaybookSectionId, AttainmentTrendResult> | undefined;
    readonly averageScore:
      | Record<PlaybookSectionId, PeriodAverageScores>
      | undefined;
    readonly averageScoreDeltaWithPrevious:
      | Record<PlaybookSectionId, PeriodAverageScoreDeltas>
      | undefined;
    readonly isLoading: boolean;
  };
  readonly customerQuotes:
    | readonly {
        readonly quote: string;
        readonly teammate_id: TeammateId;
        readonly account_id: AccountId;
        readonly grading_id?: GradingId;
      }[]
    | undefined;
  readonly highImpactCriteriaIds: readonly string[] | undefined;
  readonly isNotFound: boolean;
}

const NULL_PERIOD_SCORES: TrendPeriodScores = {
  starting_score: null,
  current_score: null,
};

/**
 * Wraps a single TrendPeriodScores into a full TeammateAttainmentTrends,
 * placing the scores under the "last_30_days" key and nulling out other periods.
 */
function wrapTrendPeriodScores(
  scores: TrendPeriodScores,
): TeammateAttainmentTrends {
  return {
    last_7_days: NULL_PERIOD_SCORES,
    last_14_days: NULL_PERIOD_SCORES,
    last_30_days: scores,
    last_90_days: NULL_PERIOD_SCORES,
    last_180_days: NULL_PERIOD_SCORES,
  };
}

/**
 * Pivots node attainment from per-teammate to per-node grouping.
 * Record<TeammateId, Record<NodeId, TrendPeriodScores>>
 *   → Record<NodeId, Record<TeammateId, TeammateAttainmentTrends>>
 */
function pivotNodeAttainment(
  source: Record<TeammateId, Record<PlaybookNodeId, TrendPeriodScores>>,
): Record<PlaybookNodeId, Record<TeammateId, TeammateAttainmentTrends>> {
  const result: Record<
    PlaybookNodeId,
    Record<TeammateId, TeammateAttainmentTrends>
  > = {};
  for (const [teammateId, nodeScores] of recordEntries(source)) {
    for (const [nodeId, scores] of recordEntries(nodeScores)) {
      if (!result[nodeId]) {
        result[nodeId] = {};
      }
      result[nodeId][teammateId] = wrapTrendPeriodScores(scores);
    }
  }
  return result;
}

function computePreviousPeriod(period: string): string {
  return DateTime.fromFormat(period, "yyyy-MM")
    .minus({ months: 1 })
    .toFormat("yyyy-MM");
}

function computeAverageScoresFromTrend(
  data: AttainmentTrendResult,
): PeriodAverageScores {
  const result = {} as Record<AttainmentTrendPeriod, number>;
  for (const period of ATTAINMENT_TREND_PERIODS) {
    const scores: number[] = [];
    for (const trends of Object.values(data)) {
      const score = trends[period].current_score;
      if (score != null) {
        scores.push(score);
      }
    }
    result[period] =
      scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  }
  return result;
}

function computeStartingAverageScoresFromTrend(
  data: AttainmentTrendResult,
): PeriodAverageScores {
  const result = {} as Record<AttainmentTrendPeriod, number>;
  for (const period of ATTAINMENT_TREND_PERIODS) {
    const scores: number[] = [];
    for (const trends of Object.values(data)) {
      const score = trends[period].starting_score;
      if (score != null) {
        scores.push(score);
      }
    }
    result[period] =
      scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  }
  return result;
}

function wrapSingleScoreAsPeriodAverages(score: number): PeriodAverageScores {
  return {
    last_7_days: 0,
    last_14_days: 0,
    last_30_days: score,
    last_90_days: 0,
    last_180_days: 0,
  };
}

function computeAbsoluteDelta(current: number, previous: number): number {
  return current - previous;
}

function wrapSingleDeltaAsPeriodDeltas(
  delta: number,
): PeriodAverageScoreDeltas {
  return {
    last_7_days: 0,
    last_14_days: 0,
    last_30_days: delta,
    last_90_days: 0,
    last_180_days: 0,
  };
}

interface DayWindow {
  readonly fromDay: DateTime<true>;
  readonly toDay: DateTime<true>;
}

/**
 * Builds the `scope` + `filters` bundle for one dashboard-time-metrics
 * request. The window's `[fromDay, toDay]` is snapped to day boundaries in
 * the window's own (local) zone: `after` to the start of `fromDay`, `before`
 * to the end of `toDay`, so any recording on the boundary days is included
 * and the server's day buckets line up with the user's calendar (the scope
 * carries the zone as `timeZone`). Selected groups are emitted as a single
 * `teammate_group` filter.
 */
function buildDashboardTimeMetricsRequest(
  playbookId: PlaybookId,
  groupIds: readonly GroupId[] | null | undefined,
  window: DayWindow,
): {
  scope: PlaybookScope;
  filters: readonly TeamPerformancePageFilter[];
} {
  const after = window.fromDay.startOf("day").toUTC().toISO();
  const before = window.toDay.endOf("day").toUTC().toISO();
  const filters: TeamPerformancePageFilter[] =
    groupIds && groupIds.length > 0
      ? [{ type: "teammate_group", ids: groupIds }]
      : [];
  return {
    scope: {
      playbookId,
      dateRange: { after: asISODateTime(after), before: asISODateTime(before) },
      timeZone: window.fromDay.zoneName,
    },
    filters,
  };
}

type DashboardTimeMetricsFields = Pick<
  UseDashboardDataResult,
  "calls" | "callLength" | "callLengthPerGreenOutcome"
>;

/**
 * Fires two requests against `/:playbookId/dashboard-time-metrics` — one
 * for the current window, one for the previous — and reshapes the two
 * responses into the `calls` / `callLength` / `callLengthPerGreenOutcome`
 * fields `UseDashboardDataResult` exposes. Splitting the comparison into
 * two requests (instead of one response with both windows baked in) lets
 * the endpoint stay a thin consumer of the standard filter bundle.
 */
function useDashboardTimeMetricsComparison({
  playbookId,
  groupIds,
  current,
  previous,
  enabled,
}: {
  readonly playbookId: PlaybookId;
  readonly groupIds: readonly GroupId[] | null | undefined;
  readonly current: DayWindow | undefined;
  readonly previous: DayWindow | undefined;
  readonly enabled: boolean;
}): DashboardTimeMetricsFields {
  const { data: currentMetrics, isLoading: isCurrentLoading } =
    usePlaybookDashboardTimeMetricsQuery(
      enabled && current
        ? buildDashboardTimeMetricsRequest(playbookId, groupIds, current)
        : skipToken,
    );

  const { data: previousMetrics, isLoading: isPreviousLoading } =
    usePlaybookDashboardTimeMetricsQuery(
      enabled && previous
        ? buildDashboardTimeMetricsRequest(playbookId, groupIds, previous)
        : skipToken,
    );

  const isLoading = isCurrentLoading || isPreviousLoading;

  return {
    calls: {
      currentTotalCount: currentMetrics?.totalCallCount,
      previousTotalCount: previousMetrics?.totalCallCount,
      trend: currentMetrics?.callVolumeTrend,
      isLoading,
    },
    callLength: {
      currentAverageSeconds: currentMetrics?.averageCallLengthSeconds,
      previousAverageSeconds: previousMetrics?.averageCallLengthSeconds,
      trendSeconds: currentMetrics?.averageCallLengthTrendSeconds,
      isLoading,
    },
    callLengthPerGreenOutcome: {
      currentAverageSeconds:
        currentMetrics?.averageCallLengthPerGreenOutcomeSeconds,
      previousAverageSeconds:
        previousMetrics?.averageCallLengthPerGreenOutcomeSeconds,
      trendSeconds:
        currentMetrics?.averageCallLengthPerGreenOutcomeTrendSeconds,
      isLoading,
    },
  };
}

/** Length of the rolling trend window when no month period is selected. */
const DASHBOARD_TIME_METRICS_TREND_DAYS = 30;

/**
 * Computes the `[fromDay, toDay]` day window pairs for the two dashboard
 * time-metric requests, given an optional month period (`yyyy-MM`). The
 * rolling view compares the trailing 30 days against the 30 days before;
 * month views compare to the prior calendar month. Days are calendar days in
 * `now`'s (local) zone, so the windows line up with the user's calendar.
 */
function computeDashboardTimeMetricsWindows(
  now: DateTime<true>,
  period: string | undefined,
): {
  current: DayWindow;
  previous: DayWindow;
} {
  if (period !== undefined) {
    const currentMonth = DateTime.fromFormat(period, "yyyy-MM", {
      zone: now.zoneName,
    });
    if (!currentMonth.isValid) {
      throw new Error(`Invalid dashboard period: ${period}`);
    }
    const currentFromDay = currentMonth.startOf("month").startOf("day");
    const currentToDay = currentMonth.endOf("month").startOf("day");
    const previousMonth = currentMonth.minus({ months: 1 });
    const previousFromDay = previousMonth.startOf("month").startOf("day");
    const previousToDay = previousMonth.endOf("month").startOf("day");
    return {
      current: { fromDay: currentFromDay, toDay: currentToDay },
      previous: { fromDay: previousFromDay, toDay: previousToDay },
    };
  }

  const currentToDay = now.startOf("day");
  const currentFromDay = currentToDay.minus({
    days: DASHBOARD_TIME_METRICS_TREND_DAYS - 1,
  });
  const previousToDay = currentFromDay.minus({ days: 1 });
  const previousFromDay = previousToDay.minus({
    days: DASHBOARD_TIME_METRICS_TREND_DAYS - 1,
  });
  return {
    current: { fromDay: currentFromDay, toDay: currentToDay },
    previous: { fromDay: previousFromDay, toDay: previousToDay },
  };
}

interface UseLast30DaysDashboardDataArgs {
  readonly playbookId: PlaybookId;
  readonly groupIds: readonly GroupId[] | null | undefined;
  readonly sectionNodeId: string | null;
  readonly enabled: boolean;
  readonly criteriaGradingValueTypes: ReadonlyMap<
    string,
    "yes_no" | "1_5_score"
  >;
  /**
   * The widest chart period currently selected by any attainment-trend card.
   * Used to size the ClickHouse query lookback window. The default
   * (`last_30_days`) keeps the query cheap; the parent widens it to
   * `last_180_days` only when a card actually shows a longer period.
   */
  readonly chartsMaxPeriod: AttainmentTrendMaxPeriod;
}

/**
 * Hook for when the "Last 30 days" tab is selected.
 * The "Last 30 days" tab behaves differently because there is no report for the current month.
 * So we fetch the current and previous data differently than for a "regular" past month.
 */
export function useLast30DaysDashboardData({
  playbookId,
  groupIds,
  sectionNodeId,
  enabled,
  criteriaGradingValueTypes,
  chartsMaxPeriod,
}: UseLast30DaysDashboardDataArgs): UseDashboardDataResult {
  const isReady = enabled && groupIds !== undefined;

  // Drive the three ClickHouse-backed headline cards ("Call volume",
  // "Average call length", "Average time per green outcome") via
  // two requests against the dashboard-time-metrics endpoint — current +
  // previous 30-day windows scoped by date filter tags. Dep on today's
  // local date so the window rolls over at local midnight if the tab is
  // left open (otherwise the windows would stay pinned to mount-time).
  const now = useNow();
  const timeMetricsWindows = useMemo(
    () => computeDashboardTimeMetricsWindows(now, undefined),
    [now],
  );
  const timeMetricsFields = useDashboardTimeMetricsComparison({
    playbookId,
    groupIds,
    current: timeMetricsWindows.current,
    previous: timeMetricsWindows.previous,
    enabled: isReady,
  });

  // Fetch playbook attainment trend
  const { data: attainmentTrend, isLoading: isAttainmentTrendLoading } =
    useDashboardPlaybookAttainmentTrendQuery(
      isReady
        ? {
            playbookId,
            groupIds,
            maxPeriod: chartsMaxPeriod,
          }
        : skipToken,
    );

  // Fetch outcome attainment trend
  const {
    data: outcomeAttainmentTrendRaw,
    isLoading: isOutcomeAttainmentTrendLoading,
  } = useDashboardPlaybookOutcomeAttainmentTrendQuery(
    isReady
      ? {
          playbookId,
          groupIds,
          maxPeriod: chartsMaxPeriod,
        }
      : skipToken,
  );
  const outcomeAttainmentTrend = outcomeAttainmentTrendRaw ?? undefined;

  // Fetch playbook sections attainment trend
  const {
    data: sectionAttainmentTrend,
    isLoading: isSectionAttainmentTrendLoading,
  } = usePlaybookSectionAttainmentTrendQuery(
    isReady && sectionNodeId
      ? {
          playbookId,
          sectionNodeId,
          groupIds,
          maxPeriod: chartsMaxPeriod,
        }
      : skipToken,
  );

  // Fetch high-impact criteria details (we only need the IDs here, but
  // the endpoint returns the full per-criterion details that the
  // playbook / teammate views consume).
  const { data: highImpactCriteriaData } = usePlaybookHighImpactCriteriaQuery(
    isReady ? { playbookId, asOfDateIso: now.endOf("day").toISO() } : skipToken,
  );
  const highImpactCriteriaIds = useMemo(
    () =>
      highImpactCriteriaData?.high_impact_criteria
        .filter((entry) => entry.is_high_impact)
        .map((entry) => entry.criteria_node_id),
    [highImpactCriteriaData],
  );

  // Fetch criteria attainment trends for high-impact criteria only, for the
  // period of data we need for the charts.
  const {
    data: criteriaAttainmentTrendsRaw,
    isLoading: isCriteriaAttainmentTrendsLoading,
  } = useDashboardCriteriaAttainmentTrendsQuery(
    isReady && highImpactCriteriaIds !== undefined
      ? {
          playbookId,
          groupIds,
          criterionIds: highImpactCriteriaIds,
          maxPeriod: chartsMaxPeriod,
        }
      : skipToken,
  );

  // Pivot criteria attainment from per-teammate to per-criterion grouping
  const criteriaAttainmentData = useMemo(():
    | Record<PlaybookCriterionId, AttainmentTrendResult>
    | undefined => {
    if (!criteriaAttainmentTrendsRaw) return undefined;
    const result: Record<
      PlaybookCriterionId,
      Record<TeammateId, TeammateAttainmentTrends>
    > = {};
    for (const [teammateId, criteriaMap] of recordEntries(
      criteriaAttainmentTrendsRaw,
    )) {
      for (const [criterionId, trends] of recordEntries(criteriaMap)) {
        if (!result[criterionId]) {
          result[criterionId] = {};
        }
        result[criterionId][teammateId] = trends;
      }
    }
    return result;
  }, [criteriaAttainmentTrendsRaw]);

  const criteriaAverageScore = useMemo(():
    | Record<PlaybookCriterionId, PeriodAverageScores>
    | undefined => {
    if (!criteriaAttainmentData) return undefined;
    const result: Record<PlaybookCriterionId, PeriodAverageScores> = {};
    for (const [criterionId, trendResult] of recordEntries(
      criteriaAttainmentData,
    )) {
      result[criterionId] = computeAverageScoresFromTrend(trendResult);
    }
    return result;
  }, [criteriaAttainmentData]);

  const criteriaStartingAverageScore = useMemo(():
    | Record<PlaybookCriterionId, PeriodAverageScores>
    | undefined => {
    if (!criteriaAttainmentData) return undefined;
    const result: Record<PlaybookCriterionId, PeriodAverageScores> = {};
    for (const [criterionId, trendResult] of recordEntries(
      criteriaAttainmentData,
    )) {
      result[criterionId] = computeStartingAverageScoresFromTrend(trendResult);
    }
    return result;
  }, [criteriaAttainmentData]);

  const criteriaAverageScoreDelta = useMemo(():
    | Record<PlaybookCriterionId, PeriodAverageScoreDeltas>
    | undefined => {
    if (!criteriaAverageScore || !criteriaStartingAverageScore)
      return undefined;
    const result: Record<PlaybookCriterionId, PeriodAverageScoreDeltas> = {};
    for (const [criterionId, currentScores] of recordEntries(
      criteriaAverageScore,
    )) {
      const startingScores = criteriaStartingAverageScore[criterionId];
      if (startingScores) {
        const gradingValueType = criteriaGradingValueTypes.get(criterionId);
        const useAbsoluteDelta = gradingValueType === "1_5_score";
        const deltas = {} as Record<AttainmentTrendPeriod, number>;
        for (const period of ATTAINMENT_TREND_PERIODS) {
          const current = currentScores[period];
          const starting = startingScores[period];
          if (useAbsoluteDelta) {
            deltas[period] = computeAbsoluteDelta(current, starting);
          } else {
            deltas[period] = computeAbsoluteDelta(current, starting);
          }
        }
        result[criterionId] = deltas;
      }
    }
    return result;
  }, [
    criteriaAverageScore,
    criteriaStartingAverageScore,
    criteriaGradingValueTypes,
  ]);

  const sectionsAttainmentData = useMemo(():
    | Record<PlaybookSectionId, AttainmentTrendResult>
    | undefined => {
    if (!sectionNodeId || !sectionAttainmentTrend) return undefined;
    return { [sectionNodeId]: sectionAttainmentTrend };
  }, [sectionNodeId, sectionAttainmentTrend]);

  const playbookAverageScore = useMemo(
    () =>
      attainmentTrend
        ? computeAverageScoresFromTrend(attainmentTrend)
        : undefined,
    [attainmentTrend],
  );

  const playbookStartingAverageScore = useMemo(
    () =>
      attainmentTrend
        ? computeStartingAverageScoresFromTrend(attainmentTrend)
        : undefined,
    [attainmentTrend],
  );

  const sectionsAverageScore = useMemo(():
    | Record<PlaybookSectionId, PeriodAverageScores>
    | undefined => {
    if (!sectionsAttainmentData) return undefined;
    const result: Record<PlaybookSectionId, PeriodAverageScores> = {};
    for (const [nodeId, trendResult] of recordEntries(sectionsAttainmentData)) {
      result[nodeId] = computeAverageScoresFromTrend(trendResult);
    }
    return result;
  }, [sectionsAttainmentData]);

  const sectionsStartingAverageScore = useMemo(():
    | Record<PlaybookSectionId, PeriodAverageScores>
    | undefined => {
    if (!sectionsAttainmentData) return undefined;
    const result: Record<PlaybookSectionId, PeriodAverageScores> = {};
    for (const [nodeId, trendResult] of recordEntries(sectionsAttainmentData)) {
      result[nodeId] = computeStartingAverageScoresFromTrend(trendResult);
    }
    return result;
  }, [sectionsAttainmentData]);

  const playbookAverageScoreDelta = useMemo(():
    | PeriodAverageScoreDeltas
    | undefined => {
    if (!playbookAverageScore || !playbookStartingAverageScore)
      return undefined;
    const result = {} as Record<AttainmentTrendPeriod, number>;
    for (const period of ATTAINMENT_TREND_PERIODS) {
      const current = playbookAverageScore[period];
      const starting = playbookStartingAverageScore[period];
      result[period] = computeAbsoluteDelta(current, starting);
    }
    return result;
  }, [playbookAverageScore, playbookStartingAverageScore]);

  const sectionsAverageScoreDelta = useMemo(():
    | Record<PlaybookSectionId, PeriodAverageScoreDeltas>
    | undefined => {
    if (!sectionsAverageScore || !sectionsStartingAverageScore)
      return undefined;
    const result: Record<PlaybookSectionId, PeriodAverageScoreDeltas> = {};
    for (const [sectionId, currentScores] of recordEntries(
      sectionsAverageScore,
    )) {
      const startingScores = sectionsStartingAverageScore[sectionId];
      if (startingScores) {
        const deltas = {} as Record<AttainmentTrendPeriod, number>;
        for (const period of ATTAINMENT_TREND_PERIODS) {
          const current = currentScores[period];
          const starting = startingScores[period];
          deltas[period] = computeAbsoluteDelta(current, starting);
        }
        result[sectionId] = deltas;
      }
    }
    return result;
  }, [sectionsAverageScore, sectionsStartingAverageScore]);

  const outcomeAverageScore = useMemo(
    () =>
      outcomeAttainmentTrend
        ? computeAverageScoresFromTrend(outcomeAttainmentTrend)
        : undefined,
    [outcomeAttainmentTrend],
  );

  const outcomeStartingAverageScore = useMemo(
    () =>
      outcomeAttainmentTrend
        ? computeStartingAverageScoresFromTrend(outcomeAttainmentTrend)
        : undefined,
    [outcomeAttainmentTrend],
  );

  const outcomeAverageScoreDelta = useMemo(():
    | PeriodAverageScoreDeltas
    | undefined => {
    if (!outcomeAverageScore || !outcomeStartingAverageScore) return undefined;
    const result = {} as Record<AttainmentTrendPeriod, number>;
    for (const period of ATTAINMENT_TREND_PERIODS) {
      const current = outcomeAverageScore[period];
      const starting = outcomeStartingAverageScore[period];
      result[period] = computeAbsoluteDelta(current, starting);
    }
    return result;
  }, [outcomeAverageScore, outcomeStartingAverageScore]);

  return {
    ...timeMetricsFields,
    playbookAttainment: {
      data: attainmentTrend,
      averageScore: playbookAverageScore,
      averageScoreDeltaWithPrevious: playbookAverageScoreDelta,
      isLoading: isAttainmentTrendLoading,
    },
    outcomeAttainment: {
      data: outcomeAttainmentTrend,
      averageScore: outcomeAverageScore,
      averageScoreDeltaWithPrevious: outcomeAverageScoreDelta,
      isLoading: isOutcomeAttainmentTrendLoading,
    },
    criteriaAttainment: {
      data: criteriaAttainmentData,
      averageScore: criteriaAverageScore,
      averageScoreDeltaWithPrevious: criteriaAverageScoreDelta,
      isLoading: isCriteriaAttainmentTrendsLoading,
    },
    sectionsAttainment: {
      data: sectionsAttainmentData,
      averageScore: sectionsAverageScore,
      averageScoreDeltaWithPrevious: sectionsAverageScoreDelta,
      isLoading: sectionNodeId != null && isSectionAttainmentTrendLoading,
    },
    customerQuotes: undefined,
    highImpactCriteriaIds,
    isNotFound: false,
  };
}

interface UseMonthDashboardDataArgs {
  readonly playbookId: PlaybookId;
  readonly period: string | undefined;
  readonly groupIds: readonly GroupId[] | null | undefined;
  readonly enabled: boolean;
  readonly criteriaGradingValueTypes: ReadonlyMap<
    string,
    "yes_no" | "1_5_score"
  >;
}

/**
 * Hook for when the regular month tab is selected.
 */
export function useMonthDashboardData({
  playbookId,
  period,
  groupIds,
  enabled,
  criteriaGradingValueTypes,
}: UseMonthDashboardDataArgs): UseDashboardDataResult {
  const isReady = enabled && period !== undefined;

  const {
    data: currentReport,
    isLoading: isCurrentReportLoading,
    error: currentReportError,
  } = usePlaybookReportQuery(
    isReady
      ? {
          playbookId,
          reportType: PlaybookReportType.MonthInReview,
          period,
        }
      : skipToken,
  );

  const { data: previousReport } = usePlaybookReportQuery(
    isReady
      ? {
          playbookId,
          reportType: PlaybookReportType.MonthInReview,
          period: computePreviousPeriod(period),
        }
      : skipToken,
  );

  // Headline cards (call volume, average call length, average time
  // per green outcome) run through the same CH-backed endpoint as the
  // rolling "Last 30 days" view, scoped to the selected calendar month +
  // prior month via `date:after` / `date:before` filter tags. The rest of
  // the dashboard (playbook / outcome / criteria attainment) continues to
  // read from the pre-computed monthly report.
  const now = useNow();
  const timeMetricsWindows = useMemo(
    () =>
      period !== undefined
        ? computeDashboardTimeMetricsWindows(now, period)
        : undefined,
    [now, period],
  );
  const timeMetricsFields = useDashboardTimeMetricsComparison({
    playbookId,
    groupIds,
    current: timeMetricsWindows?.current,
    previous: timeMetricsWindows?.previous,
    enabled: isReady,
  });

  const isNotFound =
    currentReportError != null &&
    typeof currentReportError === "object" &&
    "status" in currentReportError &&
    currentReportError.status === 404;

  // Build playbook attainment from report data:
  // Record<TeammateId, TrendPeriodScores> → Record<TeammateId, TeammateAttainmentTrends>
  // This is so we have an uniform structure for the attainment trend data.
  const playbookAttainment = useMemo((): AttainmentTrendResult | undefined => {
    const playbook_attainment = currentReport?.data.playbook_attainment;
    if (!playbook_attainment) {
      return undefined;
    }

    const result: Record<TeammateId, TeammateAttainmentTrends> = {};
    for (const [teammateId, scores] of recordEntries(playbook_attainment)) {
      // 'wrapTrendPeriodScores' will marked scores as null for
      // all periods except the last 30 days since we only compute the 30
      // days time period for a monthly report.
      result[teammateId] = wrapTrendPeriodScores(scores);
    }
    return result;
  }, [currentReport?.data.playbook_attainment]);

  // Pivot criteria attainment from report data:
  // Record<TeammateId, Record<PlaybookNodeId, TrendPeriodScores>>
  //   → Record<PlaybookNodeId, AttainmentTrendResult>
  const criteriaAttainment = useMemo(():
    | Record<PlaybookCriterionId, Record<TeammateId, TeammateAttainmentTrends>>
    | undefined => {
    const source = currentReport?.data.criteria_attainment;
    if (!source) return undefined;
    return pivotNodeAttainment(source);
  }, [currentReport?.data.criteria_attainment]);

  // Pivot sections attainment from report data
  const sectionsAttainment = useMemo(():
    | Record<PlaybookSectionId, Record<TeammateId, TeammateAttainmentTrends>>
    | undefined => {
    const source = currentReport?.data.sections_attainment;
    if (!source) return undefined;
    return pivotNodeAttainment(source);
  }, [currentReport?.data.sections_attainment]);

  // Build outcome attainment from report data
  const outcomeAttainment = useMemo((): AttainmentTrendResult | undefined => {
    const outcome_attainment = currentReport?.data.outcome_attainment;
    if (!outcome_attainment) return undefined;
    const result: Record<TeammateId, TeammateAttainmentTrends> = {};
    for (const [teammateId, scores] of recordEntries(outcome_attainment)) {
      result[teammateId] = wrapTrendPeriodScores(scores);
    }
    return result;
  }, [currentReport?.data.outcome_attainment]);

  const outcomeAverageScore = useMemo((): PeriodAverageScores | undefined => {
    const score = currentReport?.data.outcome_average_score;
    if (score == null) return undefined;
    return wrapSingleScoreAsPeriodAverages(score);
  }, [currentReport?.data.outcome_average_score]);

  const outcomeAverageScoreDelta = useMemo(():
    | PeriodAverageScoreDeltas
    | undefined => {
    const current = currentReport?.data.outcome_average_score;
    const previous = previousReport?.data.outcome_average_score;
    if (current == null || previous == null) return undefined;
    return wrapSingleDeltaAsPeriodDeltas(
      computeAbsoluteDelta(current, previous),
    );
  }, [
    currentReport?.data.outcome_average_score,
    previousReport?.data.outcome_average_score,
  ]);

  const playbookAverageScore = useMemo((): PeriodAverageScores | undefined => {
    const score = currentReport?.data.playbook_average_score;
    if (score == null) return undefined;
    return wrapSingleScoreAsPeriodAverages(score);
  }, [currentReport?.data.playbook_average_score]);

  const criteriaAverageScore = useMemo(():
    | Record<PlaybookCriterionId, PeriodAverageScores>
    | undefined => {
    const scores = currentReport?.data.criteria_average_score;
    if (!scores) return undefined;
    const result: Record<PlaybookCriterionId, PeriodAverageScores> = {};
    for (const [criterionId, score] of recordEntries(scores)) {
      result[criterionId] = wrapSingleScoreAsPeriodAverages(score);
    }
    return result;
  }, [currentReport?.data.criteria_average_score]);

  const sectionsAverageScore = useMemo(():
    | Record<PlaybookSectionId, PeriodAverageScores>
    | undefined => {
    const scores = currentReport?.data.sections_average_score;
    if (!scores) return undefined;
    const result: Record<PlaybookSectionId, PeriodAverageScores> = {};
    for (const [sectionId, score] of recordEntries(scores)) {
      result[sectionId] = wrapSingleScoreAsPeriodAverages(score);
    }
    return result;
  }, [currentReport?.data.sections_average_score]);

  const playbookAverageScoreDelta = useMemo(():
    | PeriodAverageScoreDeltas
    | undefined => {
    const current = currentReport?.data.playbook_average_score;
    const previous = previousReport?.data.playbook_average_score;
    if (current == null || previous == null) return undefined;
    return wrapSingleDeltaAsPeriodDeltas(
      computeAbsoluteDelta(current, previous),
    );
  }, [
    currentReport?.data.playbook_average_score,
    previousReport?.data.playbook_average_score,
  ]);

  const criteriaAverageScoreDelta = useMemo(():
    | Record<PlaybookCriterionId, PeriodAverageScoreDeltas>
    | undefined => {
    const currentScores = currentReport?.data.criteria_average_score;
    const previousScores = previousReport?.data.criteria_average_score;
    if (!currentScores || !previousScores) return undefined;
    const result: Record<PlaybookCriterionId, PeriodAverageScoreDeltas> = {};
    for (const [criterionId, current] of recordEntries(currentScores)) {
      const previous = previousScores[criterionId];
      if (previous != null) {
        result[criterionId] = wrapSingleDeltaAsPeriodDeltas(
          computeAbsoluteDelta(current, previous),
        );
      }
    }
    return result;
  }, [
    currentReport?.data.criteria_average_score,
    previousReport?.data.criteria_average_score,
  ]);

  const sectionsAverageScoreDelta = useMemo(():
    | Record<PlaybookSectionId, PeriodAverageScoreDeltas>
    | undefined => {
    const currentScores = currentReport?.data.sections_average_score;
    const previousScores = previousReport?.data.sections_average_score;
    if (!currentScores || !previousScores) return undefined;
    const result: Record<PlaybookSectionId, PeriodAverageScoreDeltas> = {};
    for (const [sectionId, current] of recordEntries(currentScores)) {
      const previous = previousScores[sectionId];
      if (previous != null) {
        result[sectionId] = wrapSingleDeltaAsPeriodDeltas(
          computeAbsoluteDelta(current, previous),
        );
      }
    }
    return result;
  }, [
    currentReport?.data.sections_average_score,
    previousReport?.data.sections_average_score,
  ]);

  return {
    ...timeMetricsFields,
    playbookAttainment: {
      data: playbookAttainment,
      averageScore: playbookAverageScore,
      averageScoreDeltaWithPrevious: playbookAverageScoreDelta,
      isLoading: isCurrentReportLoading,
    },
    outcomeAttainment: {
      data: outcomeAttainment,
      averageScore: outcomeAverageScore,
      averageScoreDeltaWithPrevious: outcomeAverageScoreDelta,
      isLoading: isCurrentReportLoading,
    },
    criteriaAttainment: {
      data: criteriaAttainment,
      averageScore: criteriaAverageScore,
      averageScoreDeltaWithPrevious: criteriaAverageScoreDelta,
      isLoading: isCurrentReportLoading,
    },
    sectionsAttainment: {
      data: sectionsAttainment,
      averageScore: sectionsAverageScore,
      averageScoreDeltaWithPrevious: sectionsAverageScoreDelta,
      isLoading: isCurrentReportLoading,
    },
    customerQuotes: currentReport?.data.customer_quotes,
    highImpactCriteriaIds: currentReport?.data.high_impact_criteria_ids,
    isNotFound,
  };
}
